import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FloatingButtons from '../../components/feature/FloatingButtons';
import AgendamentoModal from '../../components/feature/AgendamentoModal';
import AnimatedSection from '../../components/base/AnimatedSection';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';

export default function ContatoPage() {
  const [agendamentoOpen, setAgendamentoOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <>
      <title>Agende Consulta Dermatológica | Dra. Laura Folchini Criciúma SC</title>
      <meta name="description" content="Entre em contato com a Dra. Laura Folchini, dermatologista em Criciúma e Tubarão/SC. Agende sua consulta pelo WhatsApp (48) 99100-2443 ou pelo formulário." />
      <link rel="canonical" href="https://dralaurafolchini.com.br/contato" />
      <div className="min-h-screen bg-[#FBF8F3]">
        {agendamentoOpen && <AgendamentoModal onClose={() => setAgendamentoOpen(false)} />}
        <Navbar onAgendar={() => setAgendamentoOpen(true)} />

        {/* Hero */}
        <section className="relative flex items-center pt-16 min-h-[44vh] sm:min-h-[52vh]">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/6a4d70c093e1b77208c273c18a3b958c.jpg"
              alt="Consultório dermatológico Dra. Laura Folchini recepção elegante Criciúma SC agendamento consulta"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C130B]/80 via-[#2C1810]/55 to-[#1C130B]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />

          <div className="relative z-10 w-full max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16 py-14 sm:py-20">
            <div
              className="mb-6 opacity-0"
              style={{ animation: 'fadeInUp 0.5s ease-out 0.05s forwards' }}
            >
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm cursor-pointer group"
              >
                <i className="ri-arrow-left-line text-base group-hover:-translate-x-1 transition-transform" />
                Voltar para Home
              </Link>
            </div>

            <div className="max-w-2xl">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full mb-5 opacity-0"
                style={{ animation: 'fadeInUp 0.6s ease-out 0.15s forwards' }}
              >
                <div className="w-2 h-2 bg-[#C4A57B] rounded-full" />
                <span className="text-sm text-white/90 font-medium tracking-wide">Entre em Contato</span>
              </div>
              <h1
                className="text-4xl sm:text-5xl md:text-6xl 2xl:text-7xl font-light text-white mb-4 leading-tight opacity-0"
                style={{ animation: 'fadeInUp 0.7s ease-out 0.25s forwards' }}
              >
                Fale <span className="italic text-[#E8CFA8]">conosco</span>
              </h1>
              <p
                className="text-base sm:text-lg text-white/80 max-w-lg leading-relaxed opacity-0"
                style={{ animation: 'fadeInUp 0.7s ease-out 0.38s forwards' }}
              >
                Tire suas dúvidas, solicite informações ou agende sua consulta. Estamos à disposição para atendê-la com atenção e cuidado.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Strips */}
        <section className="bg-[#8B7355] py-4 sm:py-5">
          <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-12">
              <a
                href="https://wa.me/5548991002443"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-white/90 hover:text-white transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-[#E8CFA8] text-base" />
                </div>
                <span className="text-sm font-medium whitespace-nowrap">(48) 99100-2443</span>
              </a>
              <div className="hidden sm:block w-px h-5 bg-white/20" />
              <a
                href="mailto:contato@dralaurafolchini.com.br"
                className="flex items-center gap-2 text-white/90 hover:text-white transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-mail-line text-[#E8CFA8] text-base" />
                </div>
                <span className="text-xs sm:text-sm font-medium truncate max-w-[240px] sm:max-w-none">contato@dralaurafolchini.com.br</span>
              </a>
              <div className="hidden sm:block w-px h-5 bg-white/20" />
              <a
                href="https://wa.me/5548991002443"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-white/90 hover:text-white transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-[#E8CFA8] text-base" />
                </div>
                <span className="text-sm font-medium whitespace-nowrap">Falar pelo WhatsApp</span>
              </a>
            </div>
          </div>
        </section>

        {/* Form + Info */}
        <section className="py-12 sm:py-24 bg-[#FBF8F3]">
          <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16">
            <AnimatedSection className="mb-12 sm:mb-16">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full mb-5 border border-[#E8DCC8]">
                  <div className="w-2 h-2 bg-[#C4A57B] rounded-full" />
                  <span className="text-sm text-[#8B7355] font-medium">Atendimento Personalizado</span>
                </div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl 2xl:text-6xl font-light text-[#4A3F35] leading-tight">
                  Como podemos<br />
                  <span className="italic text-[#C4A57B]">ajudá-la?</span>
                </h2>
              </div>
            </AnimatedSection>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-start">
              <AnimatedSection direction="left">
                <ContactForm />
              </AnimatedSection>
              <AnimatedSection direction="right" delay={100}>
                <ContactInfo />
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* CTA Agendar */}
        <section className="py-16 sm:py-20 bg-white border-t border-[#E8DCC8]">
          <div className="max-w-4xl 2xl:max-w-5xl mx-auto px-4 sm:px-8 2xl:px-16 text-center">
            <AnimatedSection>
              <p className="text-sm text-[#8B7355] mb-3 font-medium tracking-wide uppercase">Pronta para o próximo passo?</p>
              <h2 className="text-3xl sm:text-4xl 2xl:text-5xl font-light text-[#4A3F35] mb-8 leading-tight">
                Agende sua <span className="italic text-[#C4A57B]">consulta</span> agora
              </h2>
              <button
                onClick={() => setAgendamentoOpen(true)}
                className="inline-flex items-center gap-2 px-10 py-4 bg-[#C4A57B] text-white text-sm font-medium rounded-full hover:bg-[#B39568] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-check-line" />
                Agendar Consulta
              </button>
            </AnimatedSection>
          </div>
        </section>

        <Footer />
        <FloatingButtons onAgendar={() => setAgendamentoOpen(true)} />
      </div>
    </>
  );
}
